import { action } from "@ember/object"
import Component from "@glimmer/component"
import type { GoogleClassroomClassSetting, RelativeDueDate } from "@shared/models/card-stack"
import type { HumanizedRelativeDates } from "cc-frontend/components/planner/google-classroom/parts/class-settings"
import type { GoogleClassroomCardEditor } from "cc-frontend/lib/google-classroom/card-editor"
import { formatISO, parseISO } from "date-fns"
import { toLeft, toRight } from "ember-animated/transitions/move-over"
import { cloneDeep, isNil } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./date-picker.module.css"

interface Signature {
  Args: {
    relativeDates: HumanizedRelativeDates | undefined
    cardEditor: GoogleClassroomCardEditor
    googleClassId: string
    classSetting: GoogleClassroomClassSetting
    dateString: string
    minimumDate: Date | undefined
    maximumDate: Date | undefined
  }
}
export default class GoogleClassroomDatePickerComponent extends Component<Signature> {
  readonly styles = styles

  @tracked center: Date = parseISO(this.args.classSetting.dueAt?.date || this.args.dateString)
  @tracked selectedDueDate: string | undefined = this.args.classSetting?.dueAt?.date
  @tracked showCalendar: boolean = this.selectedDueDate === "SPECIFIC_DATE" ? true : false
  @tracked _canShowDatePicker: boolean | null = null

  get canShowDatePicker() {
    return this.isCustomDate || this._canShowDatePicker === true
  }

  @action setCenter(date) {
    this.center = date.date
  }

  @action onSelect(date) {
    this.setRelativeDueDate(date.id)
  }

  get isCustomDate() {
    if (isNil(this.args.classSetting.dueAt)) return false
    let date = parseISO(this.args.classSetting.dueAt.date)
    return date.toString() !== "Invalid Date"
  }

  get isNotSet() {
    return this.args.classSetting.dueAt === null
  }

  rules({ newVal }) {
    if (newVal === true) {
      return toLeft
    } else {
      return toRight
    }
  }

  @action openDatePicker() {
    // set default date if it doesn't exist
    if (
      isNil(this.args.classSetting.dueAt) ||
      parseISO(this.args.classSetting.dueAt.date).toString() === "Invalid Date"
    ) {
      let dueAt = this._setDueDate(this.args.classSetting.dueAt, this.args.dateString)
      this.args.cardEditor.updateClassSetting(this.args.classSetting, "dueAt", dueAt)
    }
    this._canShowDatePicker = true
  }

  get dueDateAsDateObject() {
    if (isNil(this.args.classSetting.dueAt)) return null
    return parseISO(this.args.classSetting.dueAt.date)
  }

  @action setSpecificDate(momentDateObject) {
    let dateString = formatISO(momentDateObject.date, { representation: "date" })
    let dueAt = this._setDueDate(this.args.classSetting.dueAt, dateString)
    this.args.cardEditor.updateClassSetting(this.args.classSetting, "dueAt", dueAt)
  }

  @action setRelativeDueDate(dueDate: RelativeDueDate) {
    // in case the date picker is out.
    this._canShowDatePicker = false
    let dueAt = this._setDueDate(this.args.classSetting.dueAt, dueDate)
    this.args.cardEditor.updateClassSetting(this.args.classSetting, "dueAt", dueAt)
  }

  @action removeDueDate() {
    // in case the date picker is out.
    this._canShowDatePicker = false
    this.args.cardEditor.updateClassSetting(this.args.classSetting, "dueAt", null)
  }

  private _setDueDate(originalDueDate, dateString) {
    let dueDate = cloneDeep(originalDueDate)
    // Create it if it doesn't exist
    if (dueDate === null) {
      return {
        date: dateString,
        hour: 23,
        minute: 59,
      }

      // Otherwise, just set the date
    } else {
      dueDate.date = dateString
      return dueDate
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Menus::GoogleClassroom::DatePicker": typeof GoogleClassroomDatePickerComponent
  }
}
